import { Helmet } from 'react-helmet-async';
import { styled } from '@mui/material/styles';
import { 
  Link, 
  Container, 
  Typography, 
  Divider, 
  Stack, 
  Button,
  Box,
  Paper,
} from '@mui/material';

import useResponsive from '../hooks/useResponsive';
import { LoginForm } from '../sections/auth/login';

const StyledRoot = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledSection = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 480,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxShadow: theme.customShadows.card,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadiusLg,
  padding: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8),
  },
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  padding: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

export default function LoginPage() {
  const mdUp = useResponsive('up', 'md');

  return (
    <>
      <Helmet>
        <title>Login | ሃገሬ ጌምስ</title>
      </Helmet>

      <StyledRoot>
        {/* Logo in header - visible on all screens */}
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 16, sm: 24, md: 40 },
            left: { xs: 16, sm: 24, md: 40 },
            zIndex: 9,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            component="img"
            src="/assets/illustrations/logo.svg"
            alt="Hagere Games"
            sx={{
              width: { xs: '32px', sm: '40px', md: '48px' },
              height: 'auto',
            }}
          />
         
        </Box>

        {mdUp && (
          <StyledSection elevation={24}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 5,
                background: theme => 
                  `-webkit-linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Welcome Back
            </Typography>
            
            <Box
              component="img"
              src="/assets/illustrations/logo.svg"
              alt="login"
              sx={{
                width: '100%',
                maxWidth: 320,
                height: 'auto',
                margin: '0 auto',
                animation: 'float 6s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(-20px)' },
                },
              }}
            />
          </StyledSection>
        )}

        <Container maxWidth="sm">
          <StyledContent>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                textAlign: 'center',
                mb: 4,
                fontWeight: 700,
              }}
            >
              Sign in to Hagere Games
            </Typography>

            <Divider 
              sx={{ 
                my: 3,
                '&::before, &::after': {
                  borderColor: 'divider',
                },
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  px: 2,
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                LOGIN
              </Typography>
            </Divider>

            <LoginForm />

            <Typography 
              variant="body2" 
              align="center" 
              sx={{ 
                mt: 3,
                display: 'block',
                color: 'text.secondary',
              }}
            >
              Want to know more?{' '}
              <Link 
                variant="subtitle2" 
                href="https://t.me/hageregames"
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Hagere Games  
              </Link>
            </Typography>
          </StyledContent>
        </Container>
      </StyledRoot>
    </>
  );
}